import React from 'react';

/**
 * ClientStatCard - Premium stat card for the client portal
 * Light theme with rich animations and hover effects
 */
const ClientStatCard = ({
    title,
    value,
    subtitle,
    icon,
    color = 'indigo',
    onClick,
    loading = false,
    trend,
    pulse = false
}) => {
    const colorMap = {
        indigo: {
            bg: 'bg-gradient-to-br from-indigo-50 to-purple-50',
            border: 'border-indigo-100',
            text: 'text-indigo-600',
            iconBg: 'bg-indigo-100',
            bar: 'bg-indigo-500'
        },
        emerald: {
            bg: 'bg-gradient-to-br from-emerald-50 to-teal-50',
            border: 'border-emerald-100',
            text: 'text-emerald-600',
            iconBg: 'bg-emerald-100',
            bar: 'bg-emerald-500'
        },
        amber: {
            bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
            border: 'border-amber-100',
            text: 'text-amber-600',
            iconBg: 'bg-amber-100',
            bar: 'bg-amber-500'
        },
        rose: {
            bg: 'bg-gradient-to-br from-rose-50 to-pink-50',
            border: 'border-rose-100',
            text: 'text-rose-600',
            iconBg: 'bg-rose-100',
            bar: 'bg-rose-500'
        },
        blue: {
            bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
            border: 'border-blue-100',
            text: 'text-blue-600',
            iconBg: 'bg-blue-100',
            bar: 'bg-blue-500'
        },
        purple: {
            bg: 'bg-gradient-to-br from-purple-50 to-violet-50',
            border: 'border-purple-100',
            text: 'text-purple-600',
            iconBg: 'bg-purple-100',
            bar: 'bg-purple-500'
        }
    };

    const theme = colorMap[color] || colorMap.indigo;

    const getTrendIndicator = () => {
        if (!trend) return null;
        const { direction, value: trendValue } = trend;

        if (direction === 'up') {
            return (
                <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-600">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                    {trendValue}%
                </span>
            );
        }
        if (direction === 'down') {
            return (
                <span className="inline-flex items-center gap-0.5 text-xs font-medium text-rose-600">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                    {trendValue}%
                </span>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className={`${theme.bg} rounded-2xl border ${theme.border} p-5 animate-pulse`}>
                <div className="flex items-center gap-4">
                    <div className={`h-12 w-12 rounded-xl ${theme.iconBg} opacity-50`}></div>
                    <div className="flex-1">
                        <div className="h-3 bg-slate-200 rounded w-20 mb-2"></div>
                        <div className="h-6 bg-slate-200 rounded w-16"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`
                ${theme.bg} rounded-2xl border ${theme.border} p-5 
                relative overflow-hidden group
                transition-all duration-300 ease-out
                hover:shadow-lg hover:shadow-slate-200/50 hover:-translate-y-0.5
                ${onClick ? 'cursor-pointer' : ''}
                ${pulse ? 'ring-2 ring-rose-400 ring-opacity-50 animate-pulse' : ''}
            `}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={(e) => onClick && e.key === 'Enter' && onClick()}
        >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 opacity-10 transform translate-x-6 -translate-y-6 group-hover:opacity-20 transition-opacity">
                {icon}
            </div>

            <div className="flex items-center gap-4 relative z-10">
                {/* Icon container */}
                <div className={`h-12 w-12 rounded-xl ${theme.iconBg} flex items-center justify-center ${theme.text} flex-shrink-0`}>
                    {icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">
                            {title}
                        </p>
                        {getTrendIndicator()}
                    </div>
                    <p className={`text-2xl font-bold ${theme.text} mt-0.5`}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </p>
                    {subtitle && (
                        <p className="text-xs text-slate-500 mt-1 truncate">{subtitle}</p>
                    )}
                </div>

                {/* Arrow indicator on hover */}
                {onClick && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                )}
            </div>

            {/* Bottom accent bar */}
            <div className="mt-4 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                    className={`h-full ${theme.bar} rounded-full transition-all duration-700 ease-out`}
                    style={{ width: '100%' }}
                ></div>
            </div>
        </div>
    );
};

export default ClientStatCard;
